import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent } from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Props, State } from "./interfaces";
import { AddEditButton } from "./styles";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import { EmployeeMutationSTO } from "../../../../models/employees";
import { EmployeesCurrentMutationsApiService } from "../../../../services/employees/employees-current-mutations-api.service";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import EmployeeMutationInput from "../EmployeeMutationInput/EmployeeMutationInput";

const AddEditEmployeeMutation = ({ original, mutation, hasMutation, notifyParent }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path

  const [state, setState] = useState<State>({
    original: null,
    mutation: null,
    resetMutation: null,
    isDialogOpen: false,
    isConfirmationChangesDialogOpen: false,
    loading: false,
    error: "",
  });

  useEffect(() => {
    if (!original || !mutation) return;

    setState((state) => ({
      ...state,
      original,
      mutation,
      resetMutation: mutation,
    }));
  }, [original, mutation]);

  const setIsDialogOpen = (value: boolean) => setState((state) => ({ ...state, isDialogOpen: value }));

  const setIsConfirmationChangesDialogOpen = (value: boolean) =>
    setState((state) => ({ ...state, isConfirmationChangesDialogOpen: value }));

  const closeMainDialog = () => {
    if (!isEqual(state.mutation, state.resetMutation)) {
      setIsConfirmationChangesDialogOpen(true);
    } else setIsDialogOpen(false);
  };

  const handleDiscardChanges = () =>
    setState((state) => ({
      ...state,
      mutation,
      resetMutation: mutation,
      error: "",
    }));

  const handleSave = async (): Promise<void> => {
    setState((state) => ({ ...state, loading: true }));

    try {
      if (state.mutation) {
        const response = await new EmployeesCurrentMutationsApiService().saveItems([state.mutation]);

        toastifyResponse(response, "all", "default_success_message", "failure_saving_data", "error_saving_data");

        setState((state) => ({
          ...state,
          loading: false,
          isDialogOpen: false,
        }));

        if (notifyParent) {
          notifyParent();
        }
      }
    } catch (error: any) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error?.message ?? error,
      }));
    }
  };

  const handleChange = (field: keyof EmployeeMutationSTO, value: EmployeeMutationSTO[keyof EmployeeMutationSTO]) =>
    setState((state) => ({
      ...state,
      mutation: {
        ...state.mutation,
        [field]: value,
      } as EmployeeMutationSTO,
    }));

  return (
    <>
      <AddEditButton onClick={() => setIsDialogOpen(true)}>
        <>
          <FontAwesomeIcon icon={faChevronRight} /> &nbsp;
          {t(`${tRP}.common_keys.${hasMutation ? "edit_change_request" : "request_a_change"}`)}
        </>
      </AddEditButton>
      <Dialog
        fullWidth
        open={state.isDialogOpen}
        onClose={closeMainDialog}
      >
        <DialogTitle
          title={`${state.original?.name}: ${t(`${tRP}.my_data_and_documents.employee.edit_title`)}`}
          handleClose={closeMainDialog}
        />
        <DialogContent>
          <EmployeeMutationInput
            originalValue={state.original?.firstName}
            mutationValue={state.mutation?.firstName}
            handleChangeMutation={handleChange}
            field="firstName"
          />
          <EmployeeMutationInput
            originalValue={state.original?.lastName}
            mutationValue={state.mutation?.lastName}
            handleChangeMutation={handleChange}
            field="lastName"
          />
          <EmployeeMutationInput
            originalValue={state.original?.birthDate}
            mutationValue={state.mutation?.birthDate}
            handleChangeMutation={handleChange}
            field="birthDate"
          />
          <EmployeeMutationInput
            originalValue={state.original?.taxId}
            mutationValue={state.mutation?.taxId}
            handleChangeMutation={handleChange}
            field="taxId"
          />
        </DialogContent>
        <DialogActions
          handleCancel={closeMainDialog}
          handleSave={handleSave}
          error={state.error}
          loading={state.loading}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isConfirmationChangesDialogOpen}
        handleCancel={() => setIsConfirmationChangesDialogOpen(false)}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditEmployeeMutation;
